import { Button, ButtonRole, ButtonTargetKind } from "@components/Button";
import { CloudinaryImg } from "@components/CloudinaryImg";
import { IconSize } from "@components/Icon";
import { MarkdownText } from "@components/MarkdownText";
import { AddTipCustom } from "@components/donate/DonateV3/PaymentProcess/components/AddTip/AddTipCustom";
import { AddTipHeader } from "@components/donate/DonateV3/PaymentProcess/components/AddTip/AddTipHeader";
import { AddTipSlider } from "@components/donate/DonateV3/PaymentProcess/components/AddTip/AddTipSlider";
import { getInitialSliderValues } from "@components/donate/DonateV3/PaymentProcess/components/AddTip/DefaultTipSlider";
import { AddTipProps } from "@components/donate/DonateV3/PaymentProcess/components/AddTip/types";
import { CoverCosts } from "@components/donate/DonateV3/PaymentProcess/components/CoverCosts";
import {
  DonationSummary,
  DonationSummaryProps,
} from "@components/donate/DonateV3/PaymentProcess/components/DonationSummary";
import { useCoverCostsMode } from "@components/donate/DonateV3/PaymentProcess/useCoverCostsMode";
import styled from "@emotion/styled";
import { Big } from "big.js";
import React, { useState } from "react";

import { colorPalette } from "@every.org/common/src/display/palette";
import { ImageFormat } from "@every.org/common/src/helpers/cloudinary";

import {
  horizontalStackCss,
  spacing,
  verticalStackCss,
} from "src/theme/spacing";
import { FontWeight, TextSize, textSizeCss } from "src/theme/text";
import { useUserLayer } from "src/utility/abtesting";
import { getWindow } from "src/utility/window";

const THUMBS_UP_ICON_ID = "assets/icons/thumbs_up_g3c7zk";

const TipMessage = styled.div`
  ${horizontalStackCss.s};
  align-items: center;
  ${textSizeCss[TextSize.xs]};
  padding: ${spacing.m};
  font-weight: ${FontWeight.MEDIUM};
  background-color: #e5f2f0;
  border-radius: ${spacing.xs};
`;

function useMessageIcon({
  suggestedTipPercent,
  actualTipPercent,
}: {
  suggestedTipPercent: number;
  actualTipPercent: number;
}): { message: string; icon: string | null } {
  const tipCopyLayer = useUserLayer("tip_copy_layer", false);
  if (actualTipPercent === 0) {
    return {
      message: tipCopyLayer.get("zero_msg", "") as string,
      icon: tipCopyLayer.get("zero_icon", null) as string | null,
    };
  }

  if (actualTipPercent < suggestedTipPercent) {
    const downMsg = tipCopyLayer.get("down_msg", null);
    const downIcon = tipCopyLayer.get("down_icon", null);

    if (downMsg || downIcon) {
      return {
        message: tipCopyLayer.get(
          "down_msg",
          "Thank you. Your gift helps nonprofits everywhere."
        ) as string,
        icon: tipCopyLayer.get("down_icon", THUMBS_UP_ICON_ID) as string,
      };
    }
  } else if (actualTipPercent > suggestedTipPercent) {
    const upMsg = tipCopyLayer.get("up_msg", null);
    const upIcon = tipCopyLayer.get("up_icon", null);

    if (upMsg || upIcon) {
      return {
        message: tipCopyLayer.get(
          "up_msg",
          "Incredible! Your gift is helping grow generosity everywhere."
        ) as string,
        icon: tipCopyLayer.get("up_icon", THUMBS_UP_ICON_ID) as string,
      };
    }
  }

  return {
    message: tipCopyLayer.get(
      "message",
      "Thank you. Together, we’re helping millions achieve greater good."
    ) as string,
    icon: tipCopyLayer.get("icon", THUMBS_UP_ICON_ID) as string,
  };
}

type AddTipAndDonationSummaryProps = AddTipProps &
  Omit<DonationSummaryProps, "onTipEdit">;

export const AddTipAndDonationSummary = (
  props: AddTipAndDonationSummaryProps
) => {
  const { formContext, form, nonprofit } = props;
  const { watch } = form;
  const tipAmount = watch("amountToEveryOrg") || 0;
  const tipAmountBig = new Big(tipAmount);

  const { initialShowSlider } = getInitialSliderValues(
    formContext.donationTipPercent
  );
  const { enableTipping } = formContext;

  const { coverCostsPercent, coverCostsUiVariant, isCoverCostsMode } =
    useCoverCostsMode({
      form,
      formContext,
      nonprofit,
    });

  const [showSlider, setShowSlider] = useState(initialShowSlider);

  const isZeroTip = tipAmountBig.eq(0);

  // Get suggested and actual tip percentages to determine if tip was adjusted up
  const suggestedTipPercent = watch("suggestedTipPercent") || 0;
  const actualTipPercent = watch("actualTipPercent") || 0;

  const { message, icon } = useMessageIcon({
    suggestedTipPercent,
    actualTipPercent,
  });

  const summaryOnEdit = () => {
    setShowSlider(false);
    getWindow()
      ?.document.getElementById("donateAmountToEveryOrgInput")
      ?.focus();
  };

  return (
    <React.Fragment>
      {enableTipping && (
        <div css={verticalStackCss.xs}>
          {isCoverCostsMode ? (
            <CoverCosts
              form={form}
              formContext={formContext}
              platformCostPercent={coverCostsPercent}
              uiVariant={coverCostsUiVariant}
              paymentOption={props.paymentOption}
            />
          ) : (
            <React.Fragment>
              <AddTipHeader nonprofit={nonprofit} />
              {showSlider ? (
                <AddTipSlider {...props} isZeroTip={isZeroTip} />
              ) : (
                <AddTipCustom {...props} />
              )}
              <Button
                css={[{ alignSelf: "flex-end" }]}
                contentCss={isZeroTip && { color: colorPalette.grayMedium }}
                data-tname={showSlider ? "EnterCustomAmount" : "UseSlider"}
                role={ButtonRole.TEXT_ONLY}
                onClick={{
                  kind: ButtonTargetKind.FUNCTION,
                  action: (e) => setShowSlider(!showSlider),
                }}
              >
                {showSlider ? "Enter custom amount" : "Use slider"}
              </Button>
              {message && (
                <TipMessage>
                  {icon && (
                    <CloudinaryImg
                      cloudinaryId={icon}
                      imageFormat={ImageFormat.svg}
                      iconSize={IconSize.X_LARGE}
                    />
                  )}
                  <div>
                    <MarkdownText
                      content={message.replace("NONPROFIT", nonprofit.name)}
                    />
                  </div>
                </TipMessage>
              )}
            </React.Fragment>
          )}
        </div>
      )}
      <DonationSummary
        {...props}
        onTipEdit={isCoverCostsMode ? undefined : summaryOnEdit}
        coverCostsMode={isCoverCostsMode}
      />
    </React.Fragment>
  );
};
